// DeviceCSVFilter – upload a CSV of device IDs to restrict the device list to those IDs.

import React, { ChangeEvent, JSX, useRef, useState } from 'react';
import { PortaledHoverPanel } from '../../shared/PortaledHoverPanel';

// ---- Types ----

type DeviceCSVFilterProps = {
  onFilterIdsChange: (ids: string[] | null) => void;
};

// ---- Helpers ----

/** Split on newlines/commas/semicolons, trim, drop empty and "deviceID" header; unique. */
function parseIdsFromText(text: string): string[] {
  const raw = text.replace(/\r/g, '\n');
  const pieces = raw.split(/[\n,;]+/);
  const ids = pieces
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && !/^deviceID$/i.test(p));
  return Array.from(new Set(ids));
}

// ---- Component ----

export default function DeviceCSVFilter(props: DeviceCSVFilterProps): JSX.Element {
  const { onFilterIdsChange } = props;

  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /** Parse file, push unique device IDs to parent; clear filter when no IDs. */
  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const ids = parseIdsFromText(text);
      if (ids.length === 0) {
        onFilterIdsChange(null);
        setFileName(null);
        return;
      }
      onFilterIdsChange(ids);
      setFileName(file.name);
    };
    reader.readAsText(file);
  }

  /** Open file picker (input is hidden). */
  function handleClickUpload() {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  }

  /** Clear CSV filter and file name. */
  function handleClear() {
    onFilterIdsChange(null);
    setFileName(null);
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv,text/plain"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Upload button + hover tooltip with format info (portaled — never clipped) */}
      <PortaledHoverPanel
        panelWidthPx={240}
        panelClassName="w-60 p-2 border rounded bg-white dark:bg-slate-900 shadow text-[11px] text-gray-700"
        panel={
          <>
            <p className="mb-1 font-semibold">CSV format</p>
            <p className="mb-1">
              Upload a batch of device IDs to view only those devices.
            </p>
            <ul className="list-disc list-inside mb-1">
              <li>One ID per line</li>
              <li>Comma-separated IDs</li>
              <li>Optional header row like &quot;deviceID&quot;</li>
            </ul>
            <p className="mb-1">Example:</p>
            <pre className="bg-gray-100 dark:bg-slate-800 p-1 rounded whitespace-pre-wrap">
              {`deviceID
6100X
6100Y
6100Z`}
            </pre>
          </>
        }
      >
        <button
          type="button"
          className="px-2 py-1 border rounded bg-gray-100 dark:bg-slate-800"
          onClick={handleClickUpload}
        >
          Upload Filter CSV
        </button>
      </PortaledHoverPanel>

      {/* Show selected file name and clear */}
      {fileName && (
        <div className="flex items-center gap-2">
          <span className="max-w-[140px] truncate" title={fileName}>
            {fileName}
          </span>
          <button
            type="button"
            className="text-[11px] underline text-gray-600 dark:text-slate-400"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
